/**
 * 基于Fun-ASR的面试语音识别适配器
 * 将语音识别服务包装成与原生SpeechRecognition API兼容的接口
 * 专门为模拟面试场景优化，替代浏览器自带语音识别
 * 
 * 注意：使用专门的FunAsrInterviewService，基于Fun-ASR-Realtime模型
 * 会议助手使用QwenAsrService，基于qwen3-asr-flash-realtime模型
 */

import FunAsrInterviewService from './funAsrInterviewService.js';

class FunAsrInterviewSpeechRecognition {
  constructor() {
    // 初始化Fun-ASR服务（使用模拟面试专用版本）
    this.funAsrService = new FunAsrInterviewService();

    // 兼容原生API的属性
    this.continuous = true;
    this.interimResults = true;
    this.lang = 'zh-CN';
    this.maxAlternatives = 1;

    // 事件处理器
    this.onstart = null;
    this.onresult = null;
    this.onerror = null;
    this.onend = null;

    // 内部状态
    this._isStarted = false;
    this._currentTranscript = '';
    this._interimTranscript = '';
    this._resultIndex = 0;

    // 绑定Fun-ASR服务的事件
    this._bindFunAsrServiceEvents();
  }

  /**
   * 绑定语音识别服务的事件
   */
  _bindFunAsrServiceEvents() {
    // 语音识别开始
    this.funAsrService.addEventListener('start', () => {
      this._isStarted = true;
      if (this.onstart) {
        this.onstart(new Event('start'));
      }
    });

    // 语音识别结果
    this.funAsrService.addEventListener('result', (data) => {
      if (this.onresult) {
        // 将Fun-ASR的结果转换为兼容原生API的格式
        const compatibleEvent = this._createCompatibleResultEvent(data);
        this.onresult(compatibleEvent);
      }
    });

    // 语音识别错误
    this.funAsrService.addEventListener('error', (data) => {
      if (this.onerror) {
        const errorEvent = new Event('error');
        errorEvent.error = data.error || 'unknown';
        errorEvent.message = data.error || '语音识别错误';
        this.onerror(errorEvent);
      }
    });

    // 语音识别结束
    this.funAsrService.addEventListener('end', () => {
      this._isStarted = false;
      if (this.onend) {
        this.onend(new Event('end'));
      }
    });
  }

  /**
   * 将Fun-ASR的结果转换为兼容原生SpeechRecognition API的格式
   */
  _createCompatibleResultEvent(data) {
    const { text, isFinal, confidence } = data;

    // 创建兼容的结果事件
    const event = new Event('result');
    event.resultIndex = this._resultIndex;

    // 创建结果数组
    const results = [];

    if (isFinal) {
      // 最终结果
      this._currentTranscript = text;
      this._interimTranscript = '';

      const result = {
        isFinal: true,
        0: {
          transcript: text,
          confidence: confidence || 0.9
        },
        length: 1
      };

      results[this._resultIndex] = result;
      this._resultIndex++;
    } else {
      // 临时结果
      this._interimTranscript = text;

      const result = {
        isFinal: false,
        0: {
          transcript: text,
          confidence: confidence || 0.7
        },
        length: 1
      };

      results[this._resultIndex] = result;
    }

    // 设置结果数组属性
    event.results = results;
    event.results.length = results.length;

    return event;
  }

  /**
   * 开始语音识别
   */
  async start(maxSentenceSilence = 5000) {
    try {
      if (this._isStarted) {
        return;
      }

      this._currentTranscript = '';
      this._interimTranscript = '';
      this._resultIndex = 0;

      // 传递maxSentenceSilence参数到底层服务
      // 默认5秒静音阈值，给用户更多思考和表达时间，避免长句被截断
      const success = await this.funAsrService.startRecognition(
        null,
        maxSentenceSilence
      );

      if (!success) {
        throw new Error('启动语音识别失败');
      }

    } catch (error) {
      if (this.onerror) {
        const errorEvent = new Event('error');
        errorEvent.error = 'not-allowed';
        errorEvent.message = error.message;
        this.onerror(errorEvent);
      }
    }
  }

  /**
   * 停止语音识别
   */
  async stop() {
    try {
      if (!this._isStarted) {
        return;
      }

      await this.funAsrService.stopRecognition();

    } catch (error) {
      if (this.onerror) {
        const errorEvent = new Event('error');
        errorEvent.error = 'aborted';
        errorEvent.message = error.message;
        this.onerror(errorEvent);
      }
    }
  }

  abort() {
    this.stop();
  }

  destroy() {
    try {
      this.stop();

      this.onstart = null;
      this.onresult = null;
      this.onerror = null;
      this.onend = null;

      if (this.funAsrService && this.funAsrService.destroy) {
        this.funAsrService.destroy();
      }
    } catch (error) {
    }
  }

  /**
   * 获取当前状态
   */
  get isStarted() {
    return this._isStarted;
  }

  /**
   * 获取当前转录文本
   */
  get currentTranscript() {
    return this._currentTranscript;
  }

  /**
   * 获取临时转录文本
   */
  get interimTranscript() {
    return this._interimTranscript;
  }
}

/**
 * 创建基于Fun-ASR的面试语音识别实例
 * 用于替代原有的createInterviewSpeechRecognition函数
 */
export function createFunAsrInterviewSpeechRecognition() {
  return new FunAsrInterviewSpeechRecognition();
}

export default FunAsrInterviewSpeechRecognition;